import {
  BarChart,
  DataTable,
  EmptyState,
  List,
  ListRow,
  Section,
  Select,
  Skeleton,
  StatCard,
  Text,
} from '@ui';
import { useTranslation } from 'react-i18next';
import { Link } from 'react-router';
import { errorMessage } from '../../../lib/errors';
import { useUrlState } from '../../../lib/useUrlState';
import { formatKg } from '../../harvest/format';
import { formatEuro } from '../../sales/format';
import { useSalesReport, type SalesReport } from '../api';
import { change, yearOptions } from '../format';
import styles from './ReportPages.module.css';

const monthShort = new Intl.DateTimeFormat('de-DE', { month: 'short' });
const monthLong = new Intl.DateTimeFormat('de-DE', { month: 'long', year: 'numeric' });

/** Revenue by month, variety, article, customer, channel and member (RPT-020 – RPT-022, RPT-024). */
export function SalesStatsPage() {
  const { t } = useTranslation();
  const thisYear = new Date().getFullYear();
  const [yearParam, setYear] = useUrlState('jahr', String(thisYear));
  const year = Number(yearParam) || thisYear;
  const report = useSalesReport(year);
  return (
    <div className={styles.page}>
      <div className={styles.year}>
        <Select
          size="sm"
          aria-label={t('reports.year')}
          value={String(year)}
          onChange={(e) => setYear(e.target.value)}
          options={yearOptions(thisYear, [year, ...(report.data?.years ?? [])])}
        />
      </div>
      {report.error ? (
        <Text tone="danger" role="alert">
          {errorMessage(report.error)}
        </Text>
      ) : !report.data ? (
        <div role="status" aria-label={t('common.loading')}>
          <Skeleton height="20rem" shape="rect" />
        </div>
      ) : (
        <Sales report={report.data} />
      )}
    </div>
  );
}

function Sales({ report }: { report: SalesReport }) {
  const { t } = useTranslation();
  const year = report.year;
  const months = report.months.map((m) => new Date(year, m.month - 1, 1));
  return (
    <>
      <section aria-label={t('reports.figures')} className={styles.figures}>
        <StatCard
          label={t('reports.sales.total', { year })}
          value={formatEuro(report.total_cents)}
          delta={change(
            report.total_cents,
            report.previous_total_cents,
            t('reports.vsPrevious', { year: year - 1 }),
          )}
        />
        <StatCard label={t('reports.sales.count')} value={report.sales} />
        <StatCard
          label={t('reports.sales.average')}
          value={report.sales > 0 ? formatEuro(Math.round(report.total_cents / report.sales)) : '–'}
        />
        <StatCard
          label={t('reports.sales.previous', { year: year - 1 })}
          value={formatEuro(report.previous_total_cents)}
        />
      </section>
      {report.total_cents === 0 && report.previous_total_cents === 0 ? (
        <EmptyState
          title={t('reports.sales.emptyTitle', { year })}
          actions={<Link to="/verkauf">{t('reports.sales.toSales')}</Link>}
        />
      ) : (
        <>
          <Section title={t('reports.sales.months')} variant="content">
            <BarChart
              label={t('reports.sales.monthsChart', { year })}
              categoryHeader={t('reports.sales.month')}
              categories={months.map((d) => monthShort.format(d).replace('.', ''))}
              categoryTitles={months.map((d) => monthLong.format(d))}
              format={(v) => formatEuro(v)}
              series={[
                { key: 'year', label: String(year), values: report.months.map((m) => m.cents) },
                {
                  key: 'previous',
                  label: String(year - 1),
                  values: report.months.map((m) => m.previous_cents),
                },
              ]}
            />
          </Section>
          <div className={styles.columns}>
            <Section title={t('reports.sales.channels')}>
              <List>
                {report.channels.map((c) => (
                  <ListRow
                    key={c.channel}
                    title={t(`reports.channel.${c.channel}`)}
                    description={`${t('reports.sales.salesCount', { count: c.sales })} · ${t(
                      'reports.previousAmount',
                      { year: year - 1, amount: formatEuro(c.previous_cents) },
                    )}`}
                    trailing={formatEuro(c.cents)}
                  />
                ))}
              </List>
            </Section>
            {report.varieties.length > 0 && (
              <Section title={t('reports.sales.varieties')}>
                <List>
                  {report.varieties.map((v) => (
                    <ListRow
                      key={v.variety.id}
                      title={v.variety.name}
                      description={t('reports.sales.varietyAmount', {
                        count: v.quantity,
                        kg: formatKg(v.grams, 1),
                      })}
                      trailing={formatEuro(v.cents)}
                    />
                  ))}
                </List>
              </Section>
            )}
          </div>
          {report.members.length > 0 && <MemberSales report={report} />}
          {report.markets.length > 0 && <MarketSales report={report} />}
          {(report.customers == null || report.customers.length > 0) && (
            <Section variant="flush" title={t('reports.sales.customers')}>
              {report.customers == null ? (
                <Text tone="muted" className={styles.empty}>
                  {t('reports.sales.noCustomerAccess')}
                </Text>
              ) : (
                <DataTable
                  caption={t('reports.sales.customers')}
                  rows={report.customers}
                  rowKey={(c) => c.customer.id}
                  rowHref={(c) => `/kunden/${c.customer.id}`}
                  columns={[
                    {
                      key: 'name',
                      header: t('reports.sales.customer'),
                      primary: true,
                      cell: (c) => c.customer.name,
                    },
                    {
                      key: 'sales',
                      header: t('reports.sales.count'),
                      align: 'end',
                      mobile: 'hidden',
                      cell: (c) => c.sales,
                    },
                    {
                      key: 'cents',
                      header: t('reports.sales.revenue'),
                      align: 'end',
                      mobile: 'trailing',
                      cell: (c) => formatEuro(c.cents),
                    },
                  ]}
                />
              )}
            </Section>
          )}
          {report.articles.length > 0 && (
            <Section
              variant="flush"
              title={t('reports.sales.articles')}
              footer={t('reports.sales.articlesHint')}
            >
              <DataTable
                caption={t('reports.sales.articles')}
                rows={report.articles}
                rowKey={(a) => a.article.id}
                columns={[
                  {
                    key: 'article',
                    header: t('reports.sales.article'),
                    primary: true,
                    cell: (a) => a.article.name,
                  },
                  {
                    key: 'variety',
                    header: t('reports.sales.variety'),
                    cell: (a) => a.variety.name,
                  },
                  {
                    key: 'quantity',
                    header: t('reports.sales.quantity'),
                    align: 'end',
                    mobile: 'hidden',
                    cell: (a) => a.quantity,
                  },
                  {
                    key: 'grams',
                    header: t('reports.sales.honey'),
                    align: 'end',
                    mobile: 'hidden',
                    cell: (a) => formatKg(a.grams, 1),
                  },
                  {
                    key: 'cents',
                    header: t('reports.sales.revenue'),
                    align: 'end',
                    mobile: 'trailing',
                    cell: (a) => formatEuro(a.cents),
                  },
                ]}
              />
            </Section>
          )}
        </>
      )}
    </>
  );
}

/** Revenue and number of sales by "Vermittelt von" (RPT-024, #26). */
function MemberSales({ report }: { report: SalesReport }) {
  const { t } = useTranslation();
  const name = (m: SalesReport['members'][number]) =>
    m.former ? t('sales.referrer.former', { name: m.member.name }) : m.member.name;
  return (
    <>
      <Section title={t('reports.sales.members')} variant="content">
        <BarChart
          label={t('reports.sales.membersChart', { year: report.year })}
          categoryHeader={t('reports.sales.member')}
          categories={report.members.map(name)}
          format={(v) => formatEuro(v)}
          series={[
            {
              key: 'revenue',
              label: t('reports.sales.revenue'),
              values: report.members.map((m) => m.cents),
            },
          ]}
        />
      </Section>
      <Section variant="flush" title={t('reports.sales.membersTable')}>
        <DataTable
          caption={t('reports.sales.membersTable')}
          rows={report.members}
          rowKey={(m) => m.member.id}
          columns={[
            {
              key: 'name',
              header: t('reports.sales.member'),
              primary: true,
              cell: name,
            },
            {
              key: 'sales',
              header: t('reports.sales.count'),
              align: 'end',
              mobile: 'subtitle',
              cell: (m) => m.sales,
            },
            {
              key: 'cents',
              header: t('reports.sales.revenue'),
              align: 'end',
              mobile: 'trailing',
              cell: (m) => formatEuro(m.cents),
            },
          ]}
        />
      </Section>
    </>
  );
}

/** Revenue per market over the season (MKT-016). */
function MarketSales({ report }: { report: SalesReport }) {
  const { t } = useTranslation();
  return (
    <>
      <Section title={t('reports.sales.markets')} variant="content">
        <BarChart
          label={t('reports.sales.marketsChart', { year: report.year })}
          categoryHeader={t('reports.sales.market')}
          categories={report.markets.map((m) => m.market.name)}
          format={(v) => formatEuro(v)}
          series={[
            {
              key: 'revenue',
              label: t('reports.sales.revenue'),
              values: report.markets.map((m) => m.cents),
            },
          ]}
        />
      </Section>
      <Section variant="flush" title={t('reports.sales.marketsTable')}>
        <DataTable
          caption={t('reports.sales.marketsTable')}
          rows={report.markets}
          rowKey={(m) => m.market.id}
          columns={[
            {
              key: 'name',
              header: t('reports.sales.market'),
              primary: true,
              cell: (m) => m.market.name,
            },
            {
              key: 'days',
              header: t('reports.sales.marketDays'),
              align: 'end',
              cell: (m) => m.days,
            },
            {
              key: 'sales',
              header: t('reports.sales.count'),
              align: 'end',
              mobile: 'subtitle',
              cell: (m) => m.sales,
            },
            {
              key: 'cents',
              header: t('reports.sales.revenue'),
              align: 'end',
              mobile: 'trailing',
              cell: (m) => formatEuro(m.cents),
            },
          ]}
        />
      </Section>
    </>
  );
}
